import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError, get, post, type Me } from './api';
import { Brand } from './SignIn';
import { EmployeesPage } from './EmployeesPage';
import { Modal, useStickyNav } from './ui';
import { RotationBoard } from './Rotation';
import { CounterMidDay } from './DayBoard';
import { ReceiptLookup } from './ReceiptLookup';
import { printingNow } from './printing';
import { StaffMessages, useUnread } from './Messages';
import { HelpPage, helpFor, startFor } from './HelpPage';
import { DayBoardPage } from './DayBoard';
import { SettingsPage } from './SettingsPage';
import { ReportsPage } from './ReportsPage';
import { MODULES } from './modules';

interface Unlocker { employeeId: number; name: string; role: string; pinLength: number; hasPin: boolean }
interface DeviceResp {
  device: { kind: 'office' | 'counter'; locationId: number; locationName: string; locationKind?: 'store' | 'office'; modules?: string[] } | null;
  people?: Unlocker[]; signedIn?: Me | null; idleMinutes?: number;
}

/**
 * The shared devices (docs/sign-in.md): first a one-time setup code; then
 *  - office computer: lock screen of the store's managers → name → PIN → "Signed in as …" bar;
 *    signs out after 10 minutes without use (60-second "Still there?"), when the window closes, and nightly.
 *  - counter screen: today's rotation; a manager or shift lead unlocks it with
 *    their PIN; it locks after 2 minutes without a tap, and at once when someone switches to the POS.
 */
export function DevicePage() {
  const [d, setD] = useState<DeviceResp | null>(null);
  const load = useCallback(async () => setD(await get<DeviceResp>('/api/device')), []);
  useEffect(() => { void load(); }, [load]);
  if (!d) return null;
  if (!d.device) return <Setup onDone={() => { history.replaceState(null, '', '/'); void load(); }} />;
  return d.device.kind === 'office' ? <Office d={d} reload={load} /> : <Counter d={d} reload={load} />;
}

function Setup({ onDone }: { onDone: () => void }) {
  const [code, setCode] = useState('');
  const [err, setErr] = useState('');
  return (
    <div className="approot"><main className="signwrap"><section className="signin">
      <Brand />
      <h1>Set up this device</h1>
      <p>An admin makes a 6-digit setup code on the Managers &amp; access page (“Set up a device…”). Type it here once; this computer or screen then remembers which store it belongs to.</p>
      <label>Setup code
        <input className="inp codeinp" inputMode="numeric" value={code} maxLength={6} placeholder="••••••" aria-label="Setup code"
          onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))} />
      </label>
      {err && <p className="signerr">{err}</p>}
      <button className="ebig" disabled={code.length !== 6} onClick={async () => {
        try { await post('/api/device/setup', { code }); onDone(); } catch (e) { setErr(e instanceof ApiError ? e.message : 'That didn’t work.'); }
      }}>Set up</button>
      <a className="elink" href="/" onClick={() => history.replaceState(null, '', '/')}>This is my own phone or computer</a>
    </section></main></div>
  );
}

/** Tap a name → type the PIN. */
function PinPad({ who, onClose, onDone }: { who: Unlocker; onClose: () => void; onDone: () => void }) {
  const [pin, setPin] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const go = async (value: string) => {
    setBusy(true); setErr('');
    try { await post('/api/device/unlock', { employeeId: who.employeeId, pin: value }); onDone(); }
    catch (e) { setPin(''); setErr(e instanceof ApiError ? e.message : 'That didn’t work.'); }
    finally { setBusy(false); }
  };
  const press = (k: string) => {
    if (busy) return;
    const next = k === '⌫' ? pin.slice(0, -1) : (pin + k).slice(0, who.pinLength);
    setPin(next);
    if (next.length === who.pinLength) void go(next);
  };
  useEffect(() => {
    const kb = (e: KeyboardEvent) => { if (/^\d$/.test(e.key)) press(e.key); else if (e.key === 'Backspace') press('⌫'); };
    window.addEventListener('keydown', kb);
    return () => window.removeEventListener('keydown', kb);
  });
  return (
    <Modal title={`Hi ${who.name.split(' ')[0]}, type your PIN`} onClose={onClose}>
      {!who.hasPin ? (
        <div style={{ fontSize: 14, lineHeight: 1.5 }}>You don’t have a PIN yet. Sign in once on your phone at <b>mywunderland.com</b> to make one, then come back.</div>
      ) : (
        <>
          <div className="codebig" aria-live="polite">{'●'.repeat(pin.length)}{'○'.repeat(who.pinLength - pin.length)}</div>
          {err && <div className="fieldnote bad" style={{ textAlign: 'center' }}>{err}</div>}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
            {['1', '2', '3', '4', '5', '6', '7', '8', '9', '', '0', '⌫'].map((k, i) =>
              k ? <button key={i} className="btn2" style={{ minHeight: 56, fontSize: 22 }} onClick={() => press(k)} aria-label={k === '⌫' ? 'Delete' : k}>{k}</button> : <span key={i} />)}
          </div>
        </>
      )}
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
        <button className="elink" onClick={onClose}>Not you? Pick a different name</button>
      </div>
    </Modal>
  );
}

function LockList({ title, sub, people, reload, fine }: { title: string; sub: string; people: Unlocker[]; reload: () => void; fine: string }) {
  const [who, setWho] = useState<Unlocker | null>(null);
  return (
    <section className="olock">
      <Brand />
      <h1>{title}</h1>
      <p>{sub}</p>
      <div className="ogrid">
        {people.map((p) => <button key={p.employeeId} className="obtn" onClick={() => setWho(p)}><b>{p.name}</b><span>{p.role}</span></button>)}
        {people.length === 0 && <p>No one can unlock this yet. Managers (and shift leads, for the counter screen) are added on the Employees page.</p>}
      </div>
      <p className="signfine">{fine}</p>
      {who && <PinPad who={who} onClose={() => setWho(null)} onDone={() => { setWho(null); reload(); }} />}
    </section>
  );
}

/** Keeps the server session alive while people are active; warns, then locks, after the idle limit. */
function useIdleLock(minutes: number, onLock: () => void, warnSeconds: number) {
  const last = useRef(Date.now());
  const pinged = useRef(Date.now());
  const [left, setLeft] = useState<number | null>(null);
  useEffect(() => {
    const mark = () => { last.current = Date.now(); };
    const evs = ['pointerdown', 'keydown', 'wheel', 'touchstart'] as const;
    evs.forEach((e) => window.addEventListener(e, mark, true));
    const t = setInterval(() => {
      const idle = (Date.now() - last.current) / 1000;
      if (idle >= minutes * 60) { onLock(); return; }
      if (warnSeconds && idle >= minutes * 60 - warnSeconds) { setLeft(Math.ceil(minutes * 60 - idle)); return; }
      setLeft(null);
      if (idle < 60 && Date.now() - pinged.current > 60_000) { pinged.current = Date.now(); void get('/api/auth/me').catch(onLock); }
    }, 1000);
    return () => { clearInterval(t); evs.forEach((e) => window.removeEventListener(e, mark, true)); };
  }, [minutes, onLock, warnSeconds]);
  const stay = () => { last.current = Date.now(); pinged.current = Date.now(); setLeft(null); void get('/api/auth/me').catch(onLock); };
  return { left, stay };
}

function Office({ d, reload }: { d: DeviceResp; reload: () => void }) {
  const dev = d.device!;
  const lock = useCallback(() => { void post('/api/device/lock').finally(reload); }, [reload]);
  // Closing the window ends the sign-in too: the office cookie lasts only while the browser is open
  // (a refresh keeps you signed in). The 10-minute idle limit and the nightly 2 AM sign-out do the rest.
  if (!d.signedIn) {
    return (
      <div className="approot"><main className="page">
        <LockList title={`${dev.locationName} office`} sub="Tap your name, then type your PIN." people={d.people ?? []} reload={reload}
          fine="This is the shared office computer. It signs you out after 10 minutes without use, when the window closes, and every night." />
      </main></div>
    );
  }
  return <OfficeIn d={d} lock={lock} />;
}

type OfficePage = 'day' | 'rotation' | 'week' | 'employees' | 'messages' | 'settings' | 'receipts' | 'help'
  | 'rdaily' | 'rweek' | 'rhistory' | 'rcinema' | 'rsettings';
const OFFICE_REPORTS: Partial<Record<OfficePage, 'daily' | 'week' | 'history' | 'cinema' | 'settings'>> = { rdaily: 'daily', rweek: 'week', rhistory: 'history', rcinema: 'cinema', rsettings: 'settings' };

function OfficeIn({ d, lock }: { d: DeviceResp; lock: () => void }) {
  const dev = d.device!;
  const me = d.signedIn!;
  const { left, stay } = useIdleLock(d.idleMinutes ?? 10, lock, 60);
  const [unread, refresh] = useUnread(true);
  // a store's office computer has the same pages as a manager's own computer, for that store (owner 10/25)
  const store = dev.locationKind !== 'office';
  const [page, setPage] = useState<OfficePage>(store ? 'day' : 'employees');
  const [helpFrom, setHelpFrom] = useState<OfficePage>('employees');
  const tabs: [OfficePage, string][] = store
    ? [['day', 'Day board'], ['rotation', 'Rotation'], ['week', 'Week summary'], ['employees', 'Employees'], ['messages', unread.staff + unread.hr ? `Messages (${unread.staff + unread.hr})` : 'Messages'], ['settings', 'Settings'], ['receipts', 'Find a receipt']]
    : [['employees', 'Employees'], ['messages', unread.staff + unread.hr ? `Messages (${unread.staff + unread.hr})` : 'Messages']];
  // other parts an admin allowed on this computer (owner 11/4: Managers & access), each with its own rules
  const mods = store ? MODULES.filter((m) => dev.modules?.includes(m.key)) : [];
  const reportsOk = mods.some((m) => m.key === 'reports') && (me.role === 'admin' || (me.level ?? 0) >= 7);
  const inReports = !!OFFICE_REPORTS[page] || (page === 'help' && !!OFFICE_REPORTS[helpFrom]);
  const [menu, setMenu] = useState(false);
  const reportTabs: [OfficePage, string][] = [['rdaily', 'Daily paperwork'], ['rweek', 'Week summary'],
    ...((me.role === 'admin' || (me.level ?? 0) >= 9) ? [['rhistory', 'History']] as [OfficePage, string][] : []), ['rcinema', 'Cinema'], ['rsettings', 'Settings']];
  const shown = inReports && reportsOk ? reportTabs : tabs;
  const board = page === 'day' || page === 'week' || page === 'rotation';
  const navRef = useStickyNav<HTMLElement>();
  return (
    <div className="approot" style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <nav ref={navRef} className="noprint tabnav" style={{ background: '#FFFFFF', borderBottom: '1px solid #D9DCD6', padding: '0 20px', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 4 }} aria-label="Sections">
        {shown.map(([p, label]) => <button key={p} className={'tabbtn' + (page === p ? ' on' : '')} onClick={() => setPage(p)}>{label}</button>)}
        <button className={'tabbtn helptab' + (page === 'help' ? ' on' : '')} onClick={() => { if (page !== 'help') setHelpFrom(page); setPage('help'); }}>Help</button>
        <div style={{ flex: '1 1 0' }} />
        <div id="navslot" style={{ display: board ? 'contents' : 'none' }} />
      </nav>
      <main className="page">
        <div className="officebar noprint"><span>🖥 {dev.locationName} office computer · Signed in as <b>{me.name}</b></span>
          {reportsOk && (
            <span className="brand" style={{ position: 'relative', fontSize: 15 }}>
              <button className="bapp appswitch" aria-expanded={menu} aria-haspopup="menu" onClick={() => setMenu((m) => !m)}>{inReports ? 'Reports' : 'Schedule'} <span className="appcaret">▾</span></button>
              {menu && (
                <div className="appmenu" role="menu" style={{ left: 0 }}>
                  <button role="menuitem" className={inReports ? '' : 'on'} onClick={() => { setMenu(false); if (inReports) setPage('day'); }}><b>Schedule{inReports ? '' : ' ✓'}</b><span className="sub">Day board, rotation, employees</span></button>
                  {mods.map((m) => (
                    <button key={m.key} role="menuitem" className={inReports ? 'on' : ''} onClick={() => { setMenu(false); if (!inReports) setPage('rdaily'); }}><b>{m.name}{inReports ? ' ✓' : ''}</b><span className="sub">{m.sub}</span></button>
                  ))}
                </div>
              )}
            </span>
          )}
          <span className="obnote">Signs out after 10 minutes without use</span><span style={{ flex: '1 1 auto' }} />
          <button className="tbtn primary" onClick={lock} style={{ minWidth: 110 }}>Sign out</button></div>
        {page === 'employees' && <EmployeesPage me={me} locationId={dev.locationId} locationName={dev.locationName} />}
        {page === 'messages' && <StaffMessages me={me} locationId={dev.locationId} onSeen={refresh} />}
        {board && <DayBoardPage key={dev.locationId} me={me} locationId={dev.locationId} view={page as 'day' | 'week' | 'rotation'} />}
        {page === 'settings' && <SettingsPage me={me} locationId={dev.locationId} />}
        {OFFICE_REPORTS[page] && reportsOk && <ReportsPage locations={[{ id: dev.locationId, name: dev.locationName, kind: 'store' }] as never} locationId={dev.locationId} view={OFFICE_REPORTS[page]}
          onView={(v) => setPage(v === 'daily' ? 'rdaily' : v === 'week' ? 'rweek' : v === 'history' ? 'rhistory' : v === 'cinema' ? 'rcinema' : 'rsettings')} />}
        {page === 'receipts' && <ReceiptLookup locations={[{ id: dev.locationId, name: dev.locationName, kind: 'store' }]} locationId={dev.locationId} />}
        {page === 'help' && <HelpPage role={me.role} where="desk" hasEmployee={!!me.employeeId}
          start={helpFrom === 'help' ? undefined : helpFrom === 'employees' && !store ? 'office-computer' : startFor(helpFor(me.role, 'desk', !!me.employeeId), helpFrom)} />}
      </main>
      {left !== null && (
        <Modal title="Still there?" onClose={stay}>
          <div style={{ fontSize: 14 }}>You’ll be signed out in {left} seconds so the next manager doesn’t work under your name. Everything you did is saved.</div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <button className="btn2" onClick={lock}>Sign out now</button>
            <button className="tbtn primary" onClick={stay}>I’m still here</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function Counter({ d, reload }: { d: DeviceResp; reload: () => void }) {
  const dev = d.device!;
  const [unlocking, setUnlocking] = useState(false);
  const [mid, setMid] = useState<string | null>(null);
  const [receipts, setReceipts] = useState(false);
  const [ver, setVer] = useState(0);
  const refresh = useCallback(() => { setVer((v) => v + 1); reload(); }, [reload]);
  const lock = useCallback(() => { setMid(null); setReceipts(false); void post('/api/device/lock').finally(refresh); }, [refresh]);
  // Switching to another program (the POS) locks it at once.
  useEffect(() => {
    if (!d.signedIn) return;
    // …but not while the print box for a receipt is open (owner 10/29)
    const away = () => { if (printingNow()) return; if (document.hidden || !document.hasFocus()) lock(); };
    const blur = () => { if (!printingNow()) lock(); };
    window.addEventListener('blur', blur);
    document.addEventListener('visibilitychange', away);
    return () => { window.removeEventListener('blur', blur); document.removeEventListener('visibilitychange', away); };
  }, [d.signedIn, lock]);
  return (
    <div className="approot"><main className="page kioskmain">
      {d.signedIn && <CounterIdle minutes={d.idleMinutes ?? 2} lock={lock} />}
      <RotationBoard kiosk={{ onUnlock: () => setUnlocking(true), onLock: lock, onMid: (date) => setMid(date), onReceipts: () => setReceipts(true), ver }} />
      {unlocking && (
        <Modal title="Who’s unlocking?" onClose={() => setUnlocking(false)}>
          <LockList title={dev.locationName} sub="Tap your name, then type your PIN." people={d.people ?? []} reload={() => { setUnlocking(false); refresh(); }}
            fine="It locks itself after 2 minutes without a tap, and when someone switches to another program." />
        </Modal>
      )}
      {receipts && d.signedIn && (
        <Modal title={`Find a receipt · ${dev.locationName}`} wide onClose={() => setReceipts(false)}>
          <ReceiptLookup locations={[{ id: dev.locationId, name: dev.locationName, kind: 'store' }]} locationId={dev.locationId} noTitle />
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}><button className="btn hbtn" onClick={() => setReceipts(false)}>Close</button></div>
        </Modal>
      )}
      {mid && d.signedIn && <CounterMidDay locationId={dev.locationId} date={mid} onClose={() => setMid(null)} onDone={() => { setMid(null); setVer((v) => v + 1); }} />}
    </main></div>
  );
}

function CounterIdle({ minutes, lock }: { minutes: number; lock: () => void }) {
  useIdleLock(minutes, lock, 0);
  return null;
}
